import React from 'react';
import { useIntl } from 'react-intl';
import Image from 'next/image';
import styles from './StructuredModuleRenderer.module.scss';
import type { WebappStructuredContentModule } from '@eventbrite/event-listing-service-client';
import classNames from 'classnames';
import { getEmbedUrl } from './utils/videoUtils';
import { LazyImage } from './components/LazyImage';
import { LazyVideo } from './components/LazyVideo';
import { LoadingStrategy, type LoadingStrategyType } from './types';

// Extract the literal types directly from WebappStructuredContentModule
type ModuleType = WebappStructuredContentModule['type']; // "text" | "image" | "video"

// Create constants using the extracted types
export const MODULE_TYPES: Record<Uppercase<ModuleType>, ModuleType> = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
} as const;

export const TEXT_ALIGNMENTS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

type TextContentProps = {
  text: string;
  alignment: (typeof TEXT_ALIGNMENTS)[keyof typeof TEXT_ALIGNMENTS];
};

type ImageContentProps = {
  url: string;
  altText?: string;
  captionText?: string;
  width?: number;
  height?: number;
  loadingStrategy?: LoadingStrategyType;
};

type VideoContentProps = {
  url: string;
  altText?: string;
  loadingStrategy?: LoadingStrategyType;
};

const getTextAlignment = (alignment: (typeof TEXT_ALIGNMENTS)[keyof typeof TEXT_ALIGNMENTS]) => {
  switch (alignment) {
    case TEXT_ALIGNMENTS.LEFT:
      return styles.textLeft;
    case TEXT_ALIGNMENTS.CENTER:
      return styles.textCenter;
    case TEXT_ALIGNMENTS.RIGHT:
      return styles.textRight;
  }
};

const TextContent = ({ text, alignment }: TextContentProps) => (
  <div
    className={getTextAlignment(alignment)}
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: text }}
  />
);

const VideoContent = ({ url, altText, loadingStrategy = LoadingStrategy.LAZY }: VideoContentProps) => {
  const { formatMessage } = useIntl();
  const shouldUseLazyLoading = loadingStrategy === LoadingStrategy.LAZY;

  return (
    <div className={styles.video}>
      {shouldUseLazyLoading ? (
        <LazyVideo src={url} altText={altText} />
      ) : (
        <iframe
          src={getEmbedUrl(url)}
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          title={formatMessage({ id: 'Video content' })}
          style={{ border: 'none' }}
        >
          {altText}
        </iframe>
      )}
    </div>
  );
};

const ImageContent = ({
  url,
  altText,
  captionText,
  width = 0,
  height = 0,
  loadingStrategy = LoadingStrategy.LAZY,
}: ImageContentProps) => {
  const shouldUseLazyLoading = loadingStrategy === LoadingStrategy.LAZY;

  return (
    <div className={styles.imageContent}>
      {shouldUseLazyLoading ? (
        width > 0 && height > 0 ? (
          <LazyImage
            src={url}
            alt={altText || ''}
            width={width}
            height={height}
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
          />
        ) : (
          <LazyImage
            src={url}
            alt={altText || ''}
            fill
            style={{ position: 'relative', height: 'auto', width: '100%' }}
            objectFit="contain"
          />
        )
      ) : width > 0 && height > 0 ? (
        <Image
          key={`eager-sized-${url}-${width}x${height}`}
          src={url}
          alt={altText || ''}
          width={width}
          height={height}
          loading="eager"
          priority={true}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
        />
      ) : (
        <div>
          <Image
            key={`eager-fill-${url}`}
            src={url}
            alt={altText || ''}
            fill
            loading="eager"
            priority={true}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            style={{ objectFit: 'contain' }}
          />
        </div>
      )}
      {captionText && <span className={styles.caption}>{captionText}</span>}
    </div>
  );
};

export type StructuredModuleRendererProps = {
  module: WebappStructuredContentModule;
  isModal?: boolean;
};

export const StructuredModuleRenderer = ({ module, isModal = false }: StructuredModuleRendererProps) => {
  const className = classNames(styles.structuredContent, styles[module.type]);
  const loadingStrategy = isModal ? LoadingStrategy.EAGER : LoadingStrategy.LAZY;

  switch (module.type) {
    case MODULE_TYPES.TEXT:
      const textContent = module as TextContentProps;
      return (
        <div className={className} data-testid="text-content">
          <TextContent text={textContent.text} alignment={textContent.alignment} />
        </div>
      );

    case MODULE_TYPES.IMAGE:
      const imageContent = module as ImageContentProps;
      return (
        <div className={className} data-testid="image-content">
          <ImageContent
            url={imageContent.url}
            altText={imageContent.altText}
            captionText={imageContent.captionText}
            width={imageContent.width}
            height={imageContent.height}
            loadingStrategy={loadingStrategy}
          />
        </div>
      );
    case MODULE_TYPES.VIDEO:
      const videoContent = module as VideoContentProps;
      return (
        <div className={className} data-testid="video-content">
          <VideoContent url={videoContent.url} altText={videoContent.altText} loadingStrategy={loadingStrategy} />
        </div>
      );
    default:
      return <></>;
  }
};
